import { Input, Select, Textarea } from '@/components/ui/FormControls';
import PurchaseItemReference from '@/components/purchases/PurchaseItemReference';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  purchaseRecordSchema,
  type PurchaseRecordValues,
  localBusinessDate,
} from '@klothiq/validation';
import { purchaseTotals, saleMoney } from '@klothiq/domain';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirm } from '@/contexts/ConfirmContext';
import { useSuppliers, useCreateSupplier } from '@/hooks/useSuppliers';
import { useOperations, useOperation } from '@/hooks/useOperations';
import { useOperationIntent } from '@/hooks/useOperationIntent';
import type { PurchaseDetail } from '@/hooks/usePurchases';
import { OperationError } from '@/lib/operations';
import PageHeader from '@/components/common/PageHeader';
import DatePicker from '@/components/common/DatePicker';
import WorkModalShell from '@/components/common/WorkModalShell';
import PurchasePaymentFields, {
  emptyPurchasePayment,
  checkedPurchasePayment,
  purchaseInputClass as input,
} from '@/components/purchases/PurchasePaymentFields';
import { toast } from 'sonner';
const line = () => ({ description: '', quantity: 1, unit_cost: 0, discount: 0, variant_id: null });
export default function PurchaseRecordForm({ purchase: p }: { purchase?: PurchaseDetail }) {
  const { can, organization } = useAuth();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const ops = useOperations();
  const suppliers = useSuppliers();
  const createSupplier = useCreateSupplier();
  const save = useOperation('save_purchase_record_v1');
  const intent = useOperationIntent(p?.id || 'purchase-new');
  const initialized = useRef(false);
  const version = useRef(p?.updated_at);
  const lock = useRef(false);
  const [error, setError] = useState(''),
    [payNow, setPayNow] = useState(false),
    [payment, setPayment] = useState(emptyPurchasePayment),
    [quick, setQuick] = useState(false),
    [supplierName, setSupplierName] = useState('');
  const form = useForm<PurchaseRecordValues>({
    resolver: zodResolver(purchaseRecordSchema),
    defaultValues: {
      supplier_id: '',
      purchase_date: localBusinessDate(organization?.timezone),
      details_mode: 'amount_only',
      entered_amount: 0,
      status: 'pending',
      delivered_on: localBusinessDate(organization?.timezone),
      invoice_reference: '',
      notes: '',
      other_charges: 0,
      payment_tracking_mode: 'untracked',
      reason: '',
      items: [],
    },
  });
  const {
    register,
    control,
    watch,
    setValue,
    reset,
    handleSubmit,
    formState: { errors },
  } = form;
  const items = useFieldArray({ control, name: 'items' });
  const v = watch();
  useEffect(() => {
    if (p && !initialized.current) {
      initialized.current = true;
      version.current = p.updated_at;
      reset({
        supplier_id: p.supplier_id,
        purchase_date: p.purchase_date,
        details_mode: p.details_mode || 'amount_only',
        entered_amount: Number(p.entered_amount ?? p.total_amount),
        status: p.status === 'received' ? 'delivered' : p.status === 'draft' ? 'draft' : 'pending',
        delivered_on: p.delivered_on || p.purchase_date,
        invoice_reference: p.supplier_invoice_no || '',
        notes: p.notes || '',
        other_charges: Number(p.other_charges),
        payment_tracking_mode: p.payment_tracking_mode || 'untracked',
        reason: '',
        items: p.purchase_items.map((i) => ({
          item_id: i.id,
          variant_id: i.variant_id,
          description: i.description || '',
          quantity: i.quantity,
          unit_cost: Number(i.unit_cost),
          discount: Number(i.discount),
        })),
      });
    }
  }, [p, reset]);
  useEffect(() => {
    if (v.status === 'draft') setPayNow(false);
  }, [v.status]);
  const total =
    v.details_mode === 'amount_only'
      ? Number(v.entered_amount || 0)
      : purchaseTotals(v.items || [], Number(v.other_charges || 0)).total;
  const disabled =
    save.isPending || ops.data?.modules.purchases !== 1 || !!p?.is_deleted || !!p?.archived_at;
  async function changeDetails(on: boolean) {
    if (
      !on &&
      items.fields.length &&
      !(await confirm({
        title: 'Use amount only?',
        description:
          'Item details will be removed from this revision and retained in history after saving.',
        confirmText: 'Use amount only',
      }))
    )
      return;
    if (on) {
      setValue('details_mode', 'itemized');
      if (!items.fields.length) items.append(line());
    } else {
      setValue('entered_amount', total);
      setValue('details_mode', 'amount_only');
      setValue('items', []);
      setValue('other_charges', 0);
    }
  }
  async function submit(values: PurchaseRecordValues, duplicate = false) {
    if (lock.current) return;
    lock.current = true;
    setError('');
    try {
      const nested = payNow
        ? checkedPurchasePayment(payment, Math.max(0, total - Number(p?.paid_amount || 0)))
        : undefined;
      const payload = {
        ...values,
        purchase_id: p?.id,
        expected_updated_at: version.current,
        entered_amount: values.details_mode === 'amount_only' ? values.entered_amount : null,
        items: values.details_mode === 'itemized' ? values.items : [],
        delivered_on: values.status === 'delivered' ? values.delivered_on : null,
        payment_tracking_mode: payNow ? 'tracked' : values.payment_tracking_mode,
        payment: nested,
        duplicate_invoice_confirmed: duplicate,
        request_key: intent.key('save'),
      };
      const result = await save.mutateAsync(payload);
      intent.complete('save');
      toast.success('Purchase saved. Inventory was not changed.');
      navigate(`/purchases/${result.purchase_id}`);
    } catch (e) {
      if (e instanceof OperationError && e.code === 'INVOICE_REVIEW_REQUIRED' && !duplicate) {
        lock.current = false;
        if (
          await confirm({
            title: 'Matching supplier invoice',
            description: e.message,
            confirmText: 'This is another purchase',
          })
        )
          return submit(values, true);
      }
      setError((e as Error).message);
    } finally {
      lock.current = false;
    }
  }
  const fieldError = (key: keyof PurchaseRecordValues) => (
    <>
      {errors[key]?.message && (
        <p role="alert" className="text-sm text-destructive">
          {String(errors[key]?.message)}
        </p>
      )}
    </>
  );
  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <PageHeader
        title={p ? 'Edit purchase record' : 'Add purchase'}
        description="Record a supplier purchase. Stock is managed separately."
        backTo={p ? `/purchases/${p.id}` : '/purchases'}
      />
      {ops.data?.modules.purchases !== 1 && (
        <p
          role="alert"
          className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm"
        >
          {ops.isLoading
            ? 'Checking purchase service…'
            : ops.error?.message ||
              'The operations database migration is required before saving purchase records.'}
        </p>
      )}
      <form onSubmit={handleSubmit((values) => submit(values))} noValidate className="space-y-5">
        <fieldset disabled={disabled} className="space-y-5 disabled:opacity-70">
          <section className="glass-card grid gap-4 p-4 sm:grid-cols-2 sm:p-6">
            <h2 className="font-semibold sm:col-span-2">Purchase details</h2>
            <div className="space-y-1">
              <label htmlFor="record-supplier" className="text-sm">
                Supplier *
              </label>
              <Controller
                control={control}
                name={'supplier_id'}
                render={({ field: selectField, fieldState: selectState }) => (
                  <Select
                    id="record-supplier"
                    {...selectField}
                    className={input}
                    aria-invalid={selectState.invalid || undefined}
                    value={selectField.value ?? ''}
                    helpLabel="Supplier *"
                  >
                    <option value="">Select supplier</option>
                    {suppliers.data?.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                    {p?.suppliers && !suppliers.data?.some((s) => s.id === p.supplier_id) && (
                      <option value={p.supplier_id}>{p.suppliers.name} (inactive)</option>
                    )}
                  </Select>
                )}
              />
              {fieldError('supplier_id')}
              {suppliers.error && (
                <p role="alert" className="text-sm text-destructive">
                  Suppliers could not load.{' '}
                  <button type="button" onClick={() => void suppliers.refetch()}>
                    Retry
                  </button>
                </p>
              )}
              {can('suppliers.create') && (
                <button
                  type="button"
                  onClick={() => setQuick(true)}
                  className="min-h-11 text-sm text-primary"
                >
                  + Add supplier
                </button>
              )}
            </div>
            <div className="space-y-1">
              <label htmlFor="record-date" className="text-sm">
                Purchase date *
              </label>
              <Controller
                control={control}
                name="purchase_date"
                render={({ field }) => (
                  <DatePicker
                    id="record-date"
                    value={field.value}
                    onChange={(x) => field.onChange(x || '')}
                    ref={field.ref}
                    helpLabel="Purchase date *"
                  />
                )}
              />
              {fieldError('purchase_date')}
            </div>
            {v.details_mode === 'amount_only' && (
              <div className="space-y-1">
                <label htmlFor="record-amount" className="text-sm">
                  Amount (₹) *
                </label>
                <Input
                  id="record-amount"
                  type="number"
                  inputMode="decimal"
                  min="0.01"
                  step="0.01"
                  {...register('entered_amount', { valueAsNumber: true })}
                  className={input}
                  helpLabel="Amount (₹) *"
                />
                {fieldError('entered_amount')}
              </div>
            )}
            <div className="space-y-1">
              <label htmlFor="record-status" className="text-sm">
                Delivery status
              </label>
              {p && !can('purchases.status') ? (
                <>
                  <Input type="hidden" {...register('status')} helpLabel="Delivery status" />
                  <Input
                    id="record-status"
                    className={input}
                    value={v.status === 'delivered' ? 'Delivered' : v.status === 'draft' ? 'Draft' : 'Pending'}
                    readOnly
                    aria-readonly="true"
                    helpLabel="Delivery status"
                  />
                </>
              ) : (
                <Controller
                  control={control}
                  name={'status'}
                  render={({ field: selectField, fieldState: selectState }) => (
                    <Select
                      id="record-status"
                      {...selectField}
                      className={input}
                      aria-invalid={selectState.invalid || undefined}
                      value={selectField.value ?? ''}
                      helpLabel="Delivery status"
                    >
                      <option value="pending">Pending</option>
                      <option value="delivered">Delivered</option>
                      <option value="draft">Draft</option>
                    </Select>
                  )}
                />
              )}
            </div>
            {v.status === 'delivered' && (
              <div className="space-y-1">
                <label htmlFor="record-delivery" className="text-sm">
                  Delivery date *
                </label>
                <Controller
                  control={control}
                  name="delivered_on"
                  render={({ field }) => (
                    <DatePicker
                      id="record-delivery"
                      value={field.value}
                      min={v.purchase_date}
                      onChange={(x) => field.onChange(x || '')}
                      ref={field.ref}
                      helpLabel="Delivery date *"
                    />
                  )}
                />
                {fieldError('delivered_on')}
              </div>
            )}
            {p?.status === 'received' && v.status !== 'delivered' && (
              <label className="space-y-1 text-sm">
                Delivery correction reason *<Input {...register('reason')} className={input} helpLabel="Delivery correction reason *" />
              </label>
            )}
            <label className="space-y-1 text-sm">
              Invoice / reference
              <Input
                {...register('invoice_reference')}
                maxLength={100}
                className={input}
                helpLabel="Invoice / reference"
              />
              {fieldError('invoice_reference')}
            </label>
            <label className="space-y-1 text-sm sm:col-span-2">
              Notes
              <Textarea {...register('notes')} maxLength={1000} rows={2} className={input} helpLabel="Notes" />
              {fieldError('notes')}
            </label>
          </section>
          <section className="glass-card space-y-4 p-4 sm:p-6">
            <label className="flex min-h-11 items-center gap-3 font-medium">
              <Input
                type="checkbox"
                checked={v.details_mode === 'itemized'}
                onChange={(e) => void changeDetails(e.target.checked)}
                className="h-5 w-5 accent-primary"
                helpLabel="Add item details"
              />
              Add item details
            </label>
            <p className="text-sm text-muted-foreground">
              Optional descriptions only. These details never add products or stock.
            </p>
            {v.details_mode === 'itemized' && (
              <>
                <div className="space-y-3">
                  {items.fields.map((f, index) => (
                    <div key={f.id} className="rounded-xl border border-border p-3">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium">Item {index + 1}</h3>
                        <button
                          type="button"
                          onClick={() => items.remove(index)}
                          className="min-h-11 px-2 text-sm text-destructive"
                        >
                          Remove
                        </button>
                      </div>
                      {can('lookup.products') && (
                        <PurchaseItemReference
                          value={v.items[index]?.variant_id}
                          onSelect={(id, description, cost) => {
                            setValue(`items.${index}.variant_id`, id);
                            if (description && !v.items[index]?.description)
                              setValue(`items.${index}.description`, description.slice(0, 300));
                            if (cost !== undefined && !v.items[index]?.unit_cost)
                              setValue(`items.${index}.unit_cost`, cost);
                          }}
                        />
                      )}
                      <label className="block space-y-1 text-sm">
                        Description *
                        <Input {...register(`items.${index}.description`)} className={input} helpLabel="Description *" />
                      </label>
                      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                        {(['quantity', 'unit_cost', 'discount'] as const).map((key, n) => (
                          <label key={key} className="space-y-1 text-sm">
                            {['Quantity', 'Price (₹)', 'Discount (₹)'][n]}
                            <Input
                              type="number"
                              min={key === 'quantity' ? 1 : 0}
                              step={key === 'quantity' ? 1 : '.01'}
                              {...register(`items.${index}.${key}`, { valueAsNumber: true })}
                              className={input}
                              helpLabel={key.replaceAll('_', ' ')}
                            />
                          </label>
                        ))}
                      </div>
                      {errors.items?.[index] && (
                        <p role="alert" className="mt-2 text-sm text-destructive">
                          {Object.values(errors.items[index] || {})
                            .map((e) => (e as { message?: string }).message)
                            .filter(Boolean)
                            .join('. ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                {fieldError('items')}
                <button
                  type="button"
                  onClick={() => items.append(line())}
                  className="min-h-11 rounded-xl border border-border px-4 text-sm"
                >
                  + Add item
                </button>
                <label className="block max-w-xs space-y-1 text-sm">
                  Other charges (₹)
                  <Input
                    type="number"
                    min="0"
                    step=".01"
                    {...register('other_charges', {
                      setValueAs: (value) => (value === '' ? 0 : Number(value)),
                    })}
                    className={input}
                    helpLabel="Other charges (₹)"
                  />
                  {fieldError('other_charges')}
                </label>
              </>
            )}
          </section>
          <section className="glass-card space-y-4 p-4 sm:p-6">
            <h2 className="font-semibold">Payment</h2>
            {can('purchases.pay') && (
              <label className="flex min-h-11 items-center gap-3">
                <Input
                  type="checkbox"
                  checked={payNow}
                  disabled={v.status === 'draft'}
                  onChange={(e) => {
                    setPayNow(e.target.checked);
                    if (e.target.checked) {
                      setValue('payment_tracking_mode', 'tracked');
                      setPayment({
                        ...emptyPurchasePayment(),
                        amount: String(Math.max(0, total - Number(p?.paid_amount || 0))),
                      });
                    }
                  }}
                  className="h-5 w-5 accent-primary"
                  helpLabel="Debit an account now"
                />
                Debit an account now
              </label>
            )}
            {payNow ? (
              <>
                <p className="text-sm text-muted-foreground">
                  This records a ledger debit; it does not initiate a bank transfer. Check that this
                  payment is not already entered in Cashbook.
                </p>
                <PurchasePaymentFields value={payment} onChange={setPayment} />
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  {Number(p?.paid_amount) > 0
                    ? 'Existing payments remain unchanged. No additional account debit.'
                    : 'Purchase entry only. No bank or cash account will change.'}
                </p>
                <label className="flex min-h-11 items-center gap-3 text-sm">
                  <Input
                    type="checkbox"
                    disabled={Number(p?.paid_amount) > 0}
                    checked={v.payment_tracking_mode === 'tracked'}
                    onChange={(e) => setValue('payment_tracking_mode', e.target.checked ? 'tracked' : 'untracked')}
                    className="h-5 w-5 accent-primary"
                    helpLabel="Track an unpaid supplier balance"
                  />
                  Track an unpaid supplier balance
                </label>
              </>
            )}
          </section>
        </fieldset>
        <div className="sticky bottom-0 z-10 rounded-xl border border-border bg-card p-4 shadow-xl">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Purchase total</p>
              <p className="text-2xl font-bold">{saleMoney(Number.isFinite(total) ? total : 0)}</p>
              <p className="text-xs text-muted-foreground">
                {payNow
                  ? `Debit ${saleMoney(Number(payment.amount) || 0)} from your selected account`
                  : 'No additional account debit'}
              </p>
            </div>
            <button
              disabled={disabled}
              className="min-h-11 rounded-xl bg-primary px-6 font-semibold text-primary-foreground disabled:opacity-50"
            >
              {save.isPending ? 'Saving…' : 'Save purchase'}
            </button>
          </div>
          {error && (
            <div role="alert" className="mt-3 space-y-2 text-sm text-destructive">
              {p && (
                <button
                  type="button"
                  className="min-h-11 underline"
                  onClick={async () => {
                    if (
                      await confirm({
                        title: 'Reload saved purchase?',
                        description:
                          'Unsaved edits will be discarded. Recover any uncertain save first.',
                        confirmText: 'Reload purchase',
                      })
                    )
                      window.location.reload();
                  }}
                >
                  Reload saved purchase
                </button>
              )}
              <p>{error}</p>
              <button
                type="button"
                className="min-h-11 underline"
                onClick={() =>
                  void intent
                    .recover('save')
                    .then((r) => {
                      if (r?.purchase_id) navigate(`/purchases/${r.purchase_id}`);
                      else setError('No completed request found. Review your details and retry.');
                    })
                    .catch((e) => setError(e.message))
                }
              >
                Recover previous save
              </button>
            </div>
          )}
        </div>
      </form>
      {quick && (
        <WorkModalShell
          open
          onClose={() => setQuick(false)}
          title="Add supplier"
          busy={createSupplier.isPending}
        >
          <h2 className="text-lg font-semibold">Add supplier</h2>
          <label className="my-4 block space-y-1 text-sm">
            Supplier name *
            <Input
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              className={input}
              helpLabel="Supplier name *"
            />
          </label>
          <div className="flex justify-end gap-2">
            <button className="min-h-11 px-4" onClick={() => setQuick(false)}>
              Cancel
            </button>
            <button
              disabled={supplierName.trim().length < 2 || createSupplier.isPending}
              className="min-h-11 rounded-xl bg-primary px-4 text-primary-foreground disabled:opacity-50"
              onClick={() =>
                void createSupplier
                  .mutateAsync({ name: supplierName.trim() })
                  .then((s) => {
                    setValue('supplier_id', s.id);
                    setQuick(false);
                    setSupplierName('');
                  })
                  .catch((e) => toast.error(e.message))
              }
            >
              Save supplier
            </button>
          </div>
        </WorkModalShell>
      )}
    </div>
  );
}
